import { useEffect } from 'react';
import './dropdown-keyboard.css';

function isVisible(field) {
  for (let element = field; element; element = element.parentElement) {
    const style = getComputedStyle(element);
    if (element.hidden || element.getAttribute('aria-hidden') === 'true' || style.display === 'none' || style.visibility === 'hidden') return false;
  }
  return true;
}

// Shared fallback for custom listboxes; native selects and existing keyboard handlers keep their behavior.
export default function DropdownKeyboard() {
  useEffect(() => {
    let pointerFocus = false;
    function pointerStart() { pointerFocus = true; }
    function pointerEnd() { pointerFocus = false; }
    function openOnFocus(event) {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      const trigger = target.closest('button[aria-haspopup="listbox"]');
      if (!trigger || trigger.hasAttribute('data-open-on-focus') || pointerFocus || trigger.disabled || trigger.getAttribute('aria-expanded') === 'true'
        || !trigger.closest('form') || trigger.closest('.rg-page, .inv-cell-edit-row')
        || trigger.parentElement.contains(event.relatedTarget)) return;
      trigger.click();
      requestAnimationFrame(() => {
        if (!trigger.isConnected || trigger.getAttribute('aria-expanded') !== 'true') return;
        const root = trigger.parentElement;
        (root.querySelector('input[type="search"]') || root.querySelector('[aria-selected="true"], [role="option"]'))?.focus();
      });
    }
    function navigate(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.isComposing
        || !['ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      let target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      if (['ArrowLeft', 'ArrowRight'].includes(event.key) && target.matches?.('[role="option"]')) {
        target = target.closest?.('.wh-form-select, .cu-phone-dial, .cn-select')?.querySelector('button[aria-haspopup="listbox"]') || target;
      }
      // In the Container form, leave a dropdown search at its text edge just like a normal field.
      if (['ArrowLeft', 'ArrowRight'].includes(event.key) && target.matches?.('.cn-select input[type="search"]')) {
        const edge = event.key === 'ArrowLeft' ? 0 : target.value.length;
        if (target.selectionStart !== edge || target.selectionEnd !== edge) return;
        target = target.closest?.('.cn-select')?.querySelector('button[aria-haspopup="listbox"]') || target;
      }
      const controlledListId = target.getAttribute?.('aria-controls');
      const menu = target.closest?.('[role="listbox"], .wh-form-select-menu, .cn-select-menu, .cu-phone-menu')
        || (controlledListId ? document.getElementById(controlledListId) : null);
      const trigger = target.closest?.('button[aria-haspopup="listbox"]');
      const horizontal = ['ArrowLeft', 'ArrowRight'].includes(event.key);
      if (horizontal && target.matches?.('input, textarea') && target.selectionStart !== null) {
        const edge = event.key === 'ArrowLeft' ? 0 : target.value.length;
        if (target.selectionStart !== edge || target.selectionEnd !== edge) return;
      }
      if (!menu && ['ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp'].includes(event.key) &&
          (target.matches?.('input:not([type="checkbox"]):not([type="radio"]), textarea, .cn-presets button[role="radio"]') || (horizontal && trigger)) &&
          !target.closest?.('.rg-page') &&
          !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.isComposing) {
        const scope = target.closest?.('form, [role="dialog"], .wh-modal-backdrop, .wh-page') || target.parentElement;
        if (!scope) return;
        const fields = Array.from(scope.querySelectorAll('input, textarea, select, button[aria-haspopup="listbox"], .cn-presets button[role="radio"]'))
          .filter(field => !field.matches(':disabled') && !field.readOnly && !['hidden', 'radio', 'checkbox', 'file'].includes(field.type)
            && !field.closest('.cn-select-menu') && field.tabIndex >= 0 && isVisible(field));
        const index = fields.indexOf(target);
        const forward = ['ArrowDown', 'ArrowRight'].includes(event.key);
        let next = fields[index + (forward ? 1 : -1)];
        if (!horizontal && !target.closest?.('.wh-form-modal') && !target.matches?.('.cn-create input[type="date"]')) {
          const source = target.getBoundingClientRect();
          if (source.height) {
            const candidates = fields.filter(field => field !== target).map(field => ({ field, rect: field.getBoundingClientRect() }))
              .filter(({ rect }) => rect.width && rect.height && (forward ? rect.top >= source.bottom : rect.bottom <= source.top));
            candidates.sort((a, b) => Math.abs(a.rect.top - source.top) - Math.abs(b.rect.top - source.top)
              || Math.abs(a.rect.left - source.left) - Math.abs(b.rect.left - source.left));
            next = candidates[0]?.field;
          }
        }
        if (index >= 0 && next) {
          event.preventDefault();
          pointerFocus = false;
          next.focus();
          if (next.matches('button[aria-haspopup="listbox"]')) {
            requestAnimationFrame(() => {
              if (!next.isConnected) return;
              const focusSearch = () => {
                if (!next.isConnected || next.getAttribute('aria-expanded') !== 'true') return;
                const root = next.parentElement;
                if (document.activeElement === next || root.contains(document.activeElement)) root.querySelector('input[type="search"]')?.focus({ preventScroll: true });
              };
              if (next.getAttribute('aria-expanded') !== 'true' && document.activeElement === next) {
                next.click();
                requestAnimationFrame(focusSearch);
              } else focusSearch();
            });
          }
          next.scrollIntoView?.({ block: 'nearest' });
        }
        return;
      }
      if (horizontal) return;
      if (target.matches?.('input, textarea') && ['Home', 'End'].includes(event.key)) return;
      if (!menu && trigger) {
        event.preventDefault();
        if (trigger.getAttribute('aria-expanded') !== 'true') trigger.click();
        // React commits the menu after the click event finishes.
        requestAnimationFrame(() => {
          let parent = trigger.parentElement;
          while (parent && !parent.querySelector('[role="listbox"]')) parent = parent.parentElement;
          const list = parent?.querySelector('[role="listbox"]');
          (parent?.querySelector('input[type="search"]') || list?.querySelector('[aria-selected="true"], .selected') || list?.querySelector('[role="option"]'))?.focus();
        });
        return;
      }
      if (!menu) return;
      const options = Array.from(menu.querySelectorAll('button[role="option"]:not(:disabled)'));
      if (!options.length) return;
      event.preventDefault();
      const current = options.indexOf(document.activeElement);
      const index = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 :
        current < 0 ? (event.key === 'ArrowUp' ? options.length - 1 : 0) :
        (current + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
      options[index].focus();
      options[index].scrollIntoView?.({ block: 'nearest' });
    }
    function navigateInput(event) {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      if (target.closest('[role="listbox"], .wh-form-select-menu, .cn-select-menu, .cu-phone-menu')) return;
      if (target.matches('input, textarea')) navigate(event);
    }
    function containDropdownScroll(event) {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      const menu = target.closest('.inv-cell-select-options, .wh-form-select-menu, .wh-menu-panel, .wh-menu-list, .cn-select-menu, .cu-phone-menu, .dash-warehouse-options');
      if (!menu || !event.deltaY || event.ctrlKey) return;
      // Short menus and scroll boundaries must not scroll the surrounding page/table.
      const atTop = menu.scrollTop <= 0;
      const atBottom = menu.scrollTop + menu.clientHeight >= menu.scrollHeight - 1;
      if ((event.deltaY < 0 && atTop) || (event.deltaY > 0 && atBottom)) event.preventDefault();
    }
    document.addEventListener('wheel', containDropdownScroll, { passive: false });
    document.addEventListener('mousedown', pointerStart, true);
    document.addEventListener('mouseup', pointerEnd, true);
    document.addEventListener('focusin', openOnFocus);
    // Handle input focus before form handlers or browser suggestion navigation.
    document.addEventListener('keydown', navigateInput, true);
    document.addEventListener('keydown', navigate);
    return () => {
      document.removeEventListener('wheel', containDropdownScroll);
      document.removeEventListener('mousedown', pointerStart, true);
      document.removeEventListener('mouseup', pointerEnd, true);
      document.removeEventListener('focusin', openOnFocus);
      document.removeEventListener('keydown', navigateInput, true);
      document.removeEventListener('keydown', navigate);
    };
  }, []);
  return null;
}
